import {
  Call,
  Email,
  FiberManualRecord as FiberManualRecordIcon,
  EventAvailable as EventAvailableIcon,
} from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import {
  Modal,
  Box,
  AppBar,
  Toolbar,
  Typography,
  Grid,
  IconButton,
  useMediaQuery,
  useTheme,
  Autocomplete,
  TextField,
  CircularProgress,
} from "@mui/material";
import { useSnackbar } from "notistack";
import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";

import { UseCreateGeminiRequest } from "0.Mine/1.Hooks/AI/Gemini";
import { useHandleCalendar } from "0.Mine/1.Hooks/business/CalendarEvent";
import { UseHandleKanban } from "0.Mine/1.Hooks/CRM/Kanban";
import { UseHandleLeads, useHandleLeadTemp } from "0.Mine/1.Hooks/CRM/Leads";
import { useBusinessContext } from "0.Mine/2.Context/businessContext";
import { useSession } from "0.Mine/2.Context/sessionContext";
import { StatusOption, Lead } from "0.Mine/3.Interfaces/lead";
import { SafeArgonButton } from "0.Mine/4.Argon/ArgonComponents";

import ActivityHistory from "./ActivityHistory/ActivityHistory";
import EmailModal from "./ActivityHistory/EmailModal";
import MeetingScheduleModal from "./CommercialeCalendarModal";
import DueDateDialog from "./DueDateDialog";
import LeadInfoForm from "./LeadInfo/LeadInfoForm";
import MobileTabs from "./MobileTabView/MobileTabs";
import { useTaskModalState } from "./SharedLogic/useTaskModalState";
import { FormData, LargeModalProps } from "./sharedTypes";
import StatusDialog from "./StatusModal";
import ValueDialog from "./ValueModal";

export interface Vendor {
  id: number;
  name: string;
  profile_pic: string;
}

interface KanbanCardResponse {
  id: number;
  lead_id: number;
  business_id: number;
  title: string;
  description: string;
  status: string;
  lead: {
    id: number;
    businessid: number;
    name: string;
    status: string;
    [key: string]: any;
  };
  [key: string]: any;
}

const TaskModal: React.FC<LargeModalProps> = ({
  open,
  onClose,
  lead,
  initialStatusOptions,
  vendors,
  cardId,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));

  // Context hooks
  const { session } = useSession();
  const { session: businessSession } = useBusinessContext();

  // API hooks
  const { editLead, createLeadCardInformation } = UseHandleLeads();
  const { assignCardToVendor, getOneKanbanCard } = UseHandleKanban();
  const { analyzeLeadWithGemini } = UseCreateGeminiRequest();

  // State from custom hook
  const {
    tabValue,
    formData,
    activities,
    showAddNoteForm,
    showAllActivities,
    newNote,
    setFormData,
    handleTabChange,
    handleInputChange,
    handleSelectChange,
    handleDateChange,
    handleSubmit,
    handleNoteInputChange,
    handleAddNote,
    setShowAddNoteForm,
    setShowAllActivities,
    setNewNote,
  } = useTaskModalState(onClose, cardId, lead);

  // Local state
  const [isLoading, setIsLoading] = useState(false);
  const [leadData, setLeadData] = useState<Lead | null>(null);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [dueDateAnchorEl, setDueDateAnchorEl] = useState<HTMLElement | null>(null);
  const [valueAnchorEl, setValueAnchorEl] = useState<HTMLElement | null>(null);
  const [statusAnchorEl, setStatusAnchorEl] = useState<HTMLElement | null>(null);
  const [status, setStatus] = useState<StatusOption | null>(lead?.status || null);
  const [statusOptions, setStatusOptions] = useState<StatusOption[]>(initialStatusOptions || []);
  const [value, setValue] = useState<number>(Number(lead?.value) || 0);
  const [probability, setProbability] = useState<number>(lead?.probability || 50);
  const [selectedCommercialista, setSelectedCommercialista] = useState<Vendor | null>(null);
  const [commerciali, setCommerciali] = useState<Vendor[]>(vendors || []);
  const { postLeadDetails, getLeadDetails } = useHandleLeadTemp();
  // Update the initial useState to properly handle date strings
  const [dueDate, setDueDate] = useState<Date | null>(() => {
    // Handle date string from API and convert to Date object
    if (lead?.dueDate) {
      // Handle different date formats - both Date objects and strings
      return lead.dueDate instanceof Date ? lead.dueDate : new Date(lead.dueDate);
    }
    return null;
  });
  const { getVendorCalendarEvents } = useHandleCalendar();

  // Add this state for the meeting schedule modal
  const [meetingModalOpen, setMeetingModalOpen] = useState(false);

  // Initialize state from props when component loads
  useEffect(() => {
    const fetchData = async () => {
      if (businessSession && businessSession.selectedBusiness) {
        try {
          // Fetch all card details at once
          const cardDetails = await getOneKanbanCard(
            businessSession.selectedBusiness.id,
            Number(cardId)
          );

          if (cardDetails) {
            // Set vendor if exists
            if (cardDetails.vendor_id && cardDetails.vendor_name) {
              setSelectedCommercialista({
                id: Number(cardDetails.vendor_id),
                name: cardDetails.vendor_name,
                profile_pic: cardDetails.vendor_pic || "",
              });
            }

            // Set status if exists
            if (cardDetails.lead?.status) {
              // Find matching status option from initialStatusOptions
              const matchingStatus = initialStatusOptions.find(
                (option) => option.label === cardDetails.lead.status
              );

              if (matchingStatus) {
                setStatus(matchingStatus);
                setFormData((prev) => ({ ...prev, statusId: matchingStatus.id }));
              } else {
                console.warn("Status found but no matching option:", cardDetails.lead.status);
              }
            }

            // Set other details from lead data
            if (cardDetails.lead) {
              setFormData((prev) => ({
                ...prev,
                name: cardDetails.lead.name || "",
                company: cardDetails.lead.company || "",
                email: cardDetails.lead.email || "",
                phone: cardDetails.lead.phone || "",
                value: cardDetails.lead.value || "",
                notes: cardDetails.lead.notes || "",
                state: cardDetails.lead.state || "",
                address: cardDetails.lead.address || "",
                city: cardDetails.lead.city || "",
                probability: cardDetails.lead.probability || 0,
              }));

              // Set due date if exists
              if (cardDetails.lead.duedate) {
                try {
                  const parsedDate = new Date(cardDetails.lead.duedate);
                  if (!isNaN(parsedDate.getTime())) {
                    setDueDate(parsedDate);
                    setFormData((prev) => ({ ...prev, dueDate: parsedDate }));
                  }
                } catch (error) {
                  console.error("Error parsing date:", cardDetails.lead.duedate, error);
                }
              }

              // Set value and probability
              if (cardDetails.lead.value) {
                setValue(Number(cardDetails.lead.value));
              }
              if (cardDetails.lead.probability) {
                setProbability(Number(cardDetails.lead.probability));
              }
            }
          }
        } catch (error) {
          console.error("Error fetching card details:", error);
          enqueueSnackbar("Error loading card details", { variant: "error" });
        }
      }
    };

    fetchData();
  }, [businessSession, cardId, initialStatusOptions]);

  // Handler functions
  const handleEmailButtonClick = () => {
    setEmailModalOpen(true);
  };

  const handleDueDateClick = (event: React.MouseEvent<HTMLElement>) => {
    setDueDateAnchorEl(event.currentTarget);
  };

  const handleDueDateClose = () => {
    setDueDateAnchorEl(null);
  };

  const handleDueDateSave = async (newDueDate: Date | null) => {
    // Update local state
    setDueDate(newDueDate);
    setFormData((prev) => ({ ...prev, dueDate: newDueDate }));
    setDueDateAnchorEl(null);

    // Create activity note if needed
    if (newDueDate && newDueDate instanceof Date && !isNaN(newDueDate.getTime())) {
      const formattedDate = newDueDate.toLocaleDateString();
      setShowAddNoteForm(true);
      setNewNote({
        title: `Prossimo task per il ${formattedDate}`,
        description: `Il prossimo task e' per le ${formattedDate}.`,
        type: "note",
      });

      if (isMobile) {
        handleTabChange(null, 1);
      }
    }

    const response = await postLeadDetails(
      businessSession.selectedBusiness.id,
      cardId,
      "duedate",
      newDueDate
    );
  };

  const handleValueClick = (event: React.MouseEvent<HTMLElement>) => {
    setValueAnchorEl(event.currentTarget);
  };

  const handleValueClose = () => {
    setValueAnchorEl(null);
  };

  const handleValueSave = async (newValue: number, newProbability: number) => {
    // Update local state
    setValue(newValue);
    setProbability(newProbability);
    setFormData((prev) => ({ ...prev, value: newValue, probability: newProbability }));
    // Send to API
    await postLeadDetails(
      businessSession.selectedBusiness.id,
      cardId,
      "probability",
      newProbability
    );
    await postLeadDetails(businessSession.selectedBusiness.id, cardId, "value", newValue);
  };

  const handleScheduleMeeting = async () => {
    if (selectedCommercialista || formData.vendorId) {
      await getVendorCalendarEvents(
        businessSession.selectedBusiness.id,
        selectedCommercialista?.id
      );
      setMeetingModalOpen(true);
    } else {
      enqueueSnackbar("Seleziona prima un commerciale per pianificare un meeting", {
        variant: "warning",
      });
    }
  };

  // Add this function to handle the meeting scheduling
  const handleMeetingScheduled = async (date: Date, title: string, duration: number) => {
    try {
      // Here you would typically call an API to save the meeting
      // For now, we'll just show a success message and create a note

      const formattedDate = date.toLocaleString("it-IT", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });

      // Create an activity note about the scheduled meeting
      setShowAddNoteForm(true);
      setNewNote({
        title: `Meeting pianificato: ${title}`,
        description: `Meeting pianificato per il ${formattedDate} della durata di ${duration} minuti.`,
        type: "meeting",
      });

      // If on mobile, switch to the notes tab
      if (isMobile) {
        handleTabChange(null, 1);
      }

      enqueueSnackbar("Meeting pianificato con successo!", { variant: "success" });
    } catch (error) {
      console.error("Error scheduling meeting:", error);
      enqueueSnackbar("Errore nella pianificazione del meeting", { variant: "error" });
    }
  };

  const handleStatusClick = (event: React.MouseEvent<HTMLElement>) => {
    setStatusAnchorEl(event.currentTarget);
  };

  const handleStatusClose = () => {
    setStatusAnchorEl(null);
  };

  const handleStatusSave = async (newStatus: StatusOption | null) => {
    // Update local state
    setStatus(newStatus);
    setFormData((prev) => ({ ...prev, statusId: newStatus?.id || null }));

    // Send to API
    await postLeadDetails(
      businessSession.selectedBusiness.id,
      cardId,
      "status",
      newStatus?.label || ""
    );
  };

  const examineWithAI = async () => {
    try {
      setIsLoading(true);

      if (!businessSession?.selectedBusiness?.id) {
        throw new Error("No business selected");
      }

      // Call AI analysis
      const response = await analyzeLeadWithGemini(
        businessSession.selectedBusiness.id.toString(),
        formData,
        activities
      );

      // Update UI with response
      enqueueSnackbar("Analysis complete", { variant: "success" });
      setNewNote({
        title: `AI Analysis - ${new Date().toLocaleDateString()}`,
        description: response,
        type: "note",
      });
    } catch (error) {
      console.error("AI analysis error:", error);
      enqueueSnackbar("Failed to analyze lead", { variant: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  const assignToVendor = async () => {
    try {
      if (!businessSession?.selectedBusiness?.id) {
        throw new Error("No business selected");
      }

      const formattedData: FormData = {
        name: formData.name,
        company: formData.company,
        email: formData.email,
        phone: formData.phone,
        lastContact: formData.lastContact,
        value: formData.value,
        notes: formData.notes,
        state: formData.state,
        leadSource: formData.leadSource,
        dueDate: formData.dueDate,
        statusId: formData.statusId,
        probability: formData.probability,
        vendorId: selectedCommercialista?.id?.toString() || "",
        vendorName: selectedCommercialista?.name || "",
        address: formData.address,
        city: formData.city,
      };

      await createLeadCardInformation(
        businessSession.selectedBusiness.id,
        cardId,
        formattedData // Use FormData type as expected by the function
      );

      // Then assign to vendor
      await assignCardToVendor(
        businessSession.selectedBusiness.id,
        Number(cardId),
        selectedCommercialista.id
      );

      enqueueSnackbar("Lead assigned successfully", { variant: "success" });
    } catch (error) {
      console.error("Error assigning vendor:", error);
      enqueueSnackbar("Failed to assign lead", { variant: "error" });
    } finally {
    }
  };

  // Component content logic
  const leadInfoFormComponent = (
    <LeadInfoForm
      formData={formData}
      isMobile={isMobile}
      handleInputChange={handleInputChange}
      handleSelectChange={handleSelectChange}
      handleDateChange={handleDateChange}
      handleSubmit={handleSubmit}
    />
  );

  const activityHistoryComponent = (
    <ActivityHistory
      activities={activities}
      isMobile={isMobile}
      showAddNoteForm={showAddNoteForm}
      showAllActivities={showAllActivities}
      newNote={newNote}
      leadName={formData.name}
      setShowAddNoteForm={setShowAddNoteForm}
      setShowAllActivities={setShowAllActivities}
      handleNoteInputChange={handleNoteInputChange}
      handleAddNote={handleAddNote}
      setNewNote={setNewNote}
      analyzeActivities={examineWithAI}
    />
  );

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        sx={{
          "& .MuiBackdrop-root": {
            backgroundColor: "rgba(0, 0, 0, 0.7)",
          },
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: "100%",
            height: "100%",
            m: 0,
            p: 0,
            bgcolor: "background.paper",
            display: "flex",
            flexDirection: "column",
            borderRadius: 0,
            boxShadow: "none",
            overflow: "hidden",
          }}
        >
          {/* Header */}
          <AppBar
            position="static"
            sx={{
              boxShadow: 2,
              "& .MuiToolbar-root": {
                minHeight: isMobile ? "56px" : "64px",
                padding: isMobile ? "0 8px" : isTablet ? "0 16px" : "0 24px",
              },
            }}
          >
            <Toolbar>
              <Typography variant={isMobile ? "subtitle1" : "h6"} sx={{ flexGrow: 1 }}>
                {lead?.name ? `${lead.name}` : "New Lead"}
              </Typography>

              <IconButton
                edge="end"
                color="inherit"
                onClick={onClose}
                size={isMobile ? "small" : "medium"}
                aria-label="close"
              >
                <CloseIcon />
              </IconButton>
            </Toolbar>

            {/* Action buttons */}
            <Box
              sx={{
                p: isMobile ? 1 : 2,
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                alignItems: isMobile ? "stretch" : "center",
                gap: 1,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  flexWrap: "wrap",
                  justifyContent: isMobile ? "center" : "flex-start",
                }}
              >
                <SafeArgonButton
                  color="primary"
                  variant="contained"
                  size="small"
                  onClick={handleDueDateClick}
                >
                  Data prossima attività
                  {dueDate && dueDate instanceof Date && !isNaN(dueDate.getTime()) && (
                    <Box component="span" sx={{ ml: 1 }}>
                      ({dueDate.toLocaleDateString()})
                    </Box>
                  )}
                </SafeArgonButton>
                <SafeArgonButton
                  color="primary"
                  variant="contained"
                  size="small"
                  onClick={handleStatusClick}
                >
                  Esito
                  {status && (
                    <Box component="span" sx={{ ml: 1, display: "flex", alignItems: "center" }}>
                      <FiberManualRecordIcon sx={{ color: status.color, fontSize: 12, mr: 0.5 }} />
                      {status.label}
                    </Box>
                  )}
                </SafeArgonButton>
                <SafeArgonButton
                  color="primary"
                  variant="contained"
                  size="small"
                  onClick={handleValueClick}
                >
                  Valore del Lead
                  {value > 0 && (
                    <Box component="span" sx={{ ml: 1 }}>
                      (€{value})
                    </Box>
                  )}
                </SafeArgonButton>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  ml: isMobile ? 0 : "auto",
                  justifyContent: isMobile ? "center" : "flex-end",
                  mt: isMobile ? 1 : 0,
                }}
              >
                {/* Add the commercial assignment dropdown here */}
                {(session?.user?.qlickup === "user" || session?.user?.qlickup === "admin") && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      maxWidth: isMobile ? "100%" : "300px",
                    }}
                  >
                    <Autocomplete
                      options={commerciali}
                      getOptionLabel={(option) => option.name}
                      value={selectedCommercialista}
                      onChange={(event, newValue) => {
                        setSelectedCommercialista(newValue);
                        if (newValue) {
                          // Optionally auto-assign when selecting
                          // assignToVendor();
                        }
                      }}
                      renderOption={(props, option) => (
                        <Box
                          component="li"
                          {...props}
                          sx={{ display: "flex", alignItems: "center" }}
                        >
                          <img
                            src={option.profile_pic}
                            alt={option.name}
                            width={24}
                            height={24}
                            style={{ borderRadius: "50%", marginRight: 8 }}
                          />
                          {option.name}
                        </Box>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Assegna a"
                          variant="outlined"
                          size="small"
                          sx={{ minWidth: "150px" }}
                          InputLabelProps={{
                            sx: {
                              transform: "translate(11px, 1px) scale(1)",
                              transition: "all 0.2s ease",
                              "&.MuiInputLabel-shrink": {
                                transform: "translate(14px, -9px) scale(0.75)",
                              },
                            },
                          }}
                        />
                      )}
                      isOptionEqualToValue={(option, value) => option.id === value?.id}
                      size="small"
                    />
                    <SafeArgonButton
                      color="info"
                      variant="contained"
                      size="small"
                      disabled={!selectedCommercialista}
                      onClick={assignToVendor}
                    >
                      Assegna
                    </SafeArgonButton>
                  </Box>
                )}

                {/* Show meeting button only when commercialista is selected */}
                {(formData.vendorId || selectedCommercialista) && (
                  <SafeArgonButton
                    color="success"
                    variant="contained"
                    size="small"
                    onClick={handleScheduleMeeting}
                  >
                    <EventAvailableIcon />
                    {!isMobile && (
                      <Box component="span" sx={{ ml: 1 }}>
                        Assegna meeting
                      </Box>
                    )}
                  </SafeArgonButton>
                )}

                <SafeArgonButton color="primary" variant="contained" size="small">
                  <Call />
                  {!isMobile && (
                    <Box component="span" sx={{ ml: 1 }}>
                      Chiama
                    </Box>
                  )}
                </SafeArgonButton>

                <SafeArgonButton
                  color="primary"
                  variant="contained"
                  size="small"
                  onClick={handleEmailButtonClick}
                >
                  <Email />
                  {!isMobile && (
                    <Box component="span" sx={{ ml: 1 }}>
                      Email
                    </Box>
                  )}
                </SafeArgonButton>
              </Box>
            </Box>
          </AppBar>

          {/* Main content area */}
          {isMobile ? (
            <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
              <MobileTabs
                tabValue={tabValue}
                handleTabChange={handleTabChange}
                leadInfoForm={leadInfoFormComponent}
                activityHistory={activityHistoryComponent}
              />
            </Box>
          ) : (
            <Box
              sx={{
                flex: 1,
                overflowY: "auto",
                p: isTablet ? 2 : 3,
                height: "100%",
              }}
            >
              <Grid container spacing={isTablet ? 2 : 3} sx={{ height: "100%" }}>
                <Grid item xs={12} md={6} sx={{ height: "100%" }}>
                  <Box
                    sx={{
                      height: "100%",
                      overflowY: "auto",
                      pr: { md: 1 },
                    }}
                  >
                    {leadInfoFormComponent}
                  </Box>
                </Grid>
                <Grid item xs={12} md={6} sx={{ height: "100%" }}>
                  <Box
                    sx={{
                      height: "100%",
                      overflowY: "auto",
                      pl: { md: 1 },
                    }}
                  >
                    {activityHistoryComponent}
                  </Box>
                </Grid>
              </Grid>
            </Box>
          )}
        </Box>
      </Modal>

      {/* Modals and Dialogs */}
      <EmailModal open={emailModalOpen} onClose={() => setEmailModalOpen(false)} lead={lead} />

      <DueDateDialog
        anchorEl={dueDateAnchorEl}
        open={Boolean(dueDateAnchorEl)}
        onClose={handleDueDateClose}
        onSave={handleDueDateSave}
        currentDueDate={dueDate}
      />

      <ValueDialog
        anchorEl={valueAnchorEl}
        open={Boolean(valueAnchorEl)}
        onClose={handleValueClose}
        onSave={handleValueSave}
        currentValue={value}
        currentProbability={probability}
      />

      <StatusDialog
        anchorEl={statusAnchorEl}
        open={Boolean(statusAnchorEl)}
        onClose={handleStatusClose}
        onSave={handleStatusSave}
        currentStatus={status}
        initialOptions={statusOptions}
      />

      <MeetingScheduleModal
        open={meetingModalOpen}
        onClose={() => setMeetingModalOpen(false)}
        vendor={selectedCommercialista}
        leadName={formData.name}
        onSchedule={handleMeetingScheduled}
      />

      {/* Loading overlay */}
      {isLoading && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(0, 0, 0, 0.6)",
            zIndex: 9999,
            backdropFilter: "blur(4px)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              backgroundColor: "white",
              borderRadius: 2,
              padding: 4,
              boxShadow: 3,
            }}
          >
            <CircularProgress size={60} thickness={4} sx={{ color: theme.palette.primary.main }} />
            <Typography variant="h6" sx={{ mt: 2, fontWeight: 500 }}>
              Elaborazione in corso...
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, color: "text.secondary" }}>
              Stiamo analizzando i dati, attendere prego
            </Typography>
          </Box>
        </Box>
      )}
    </>
  );
};

export default TaskModal;
